<template>
  <div class="info">
    <div class="settings-container">
      <!-- CARD — Informações do Tenant -->
      <div class="settings-card">
        <div class="card-header justify-between">
          <div class="flex align-items-center">
            <feather-icon class="card-icon" icon="InfoIcon"/>
            <span class="card-title">Informações do Tenant</span>
          </div>

          <div class="inline-flex gap-2">
            <vx-tooltip class="cursor-pointer" delay=".2s" color="gray" position="bottom" v-if="canSee('reactivate') && tenantData && isOriginBackoffice" :text="tenantData.deleted_at === null ? 'Desativar' : 'Reativar'">
              <vs-icon @click="confirmAction()" :icon="tenantData.deleted_at === null ? 'delete' : 'restore'"></vs-icon>
            </vx-tooltip>
            <vx-tooltip v-if="tenantData" class="cursor-pointer" delay=".2s" color="gray" position="bottom" text="Atualizar Dados">
              <feather-icon @click="loadTenantData()" icon="RefreshCwIcon"/>
            </vx-tooltip>
          </div>
        </div>
        <div class="card-divider"></div>
        <div class="card-content">
          <div v-if="tenantData">
            <div class="tenant-info-grid">
              <div class="info-item">
                <div class="info-label">Nome</div>
                <div class="info-value">{{ tenantData.fqdn.split('.')[0] }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">CNPJ</div>
                <div class="info-value">{{ tenantData.cnpj || 'N/A' }}</div>
              </div>
              <div class="info-item" v-if="tenantData.created_at">
                <div class="info-label">Criado em</div>
                <div class="info-value">{{ new Date(tenantData.created_at).toLocaleString('pt-BR') }}</div>
              </div>
              <div class="info-item" v-if="tenantData.deleted_at">
                <div class="info-label">Desativado em</div>
                <div class="info-value">{{ tenantDeletedAt }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Parceiro</div>
                <div class="info-value">{{ currentPartner.name || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Plano</div>
                <div class="info-value">{{ tenantData.plan || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Beta</div>
                <div class="info-value">
                  <span :class="tenantData.is_beta ? 'status-active' : 'status-inactive'">
                    {{ tenantData.is_beta ? 'Sim' : 'Não' }}
                  </span>
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Manutenção</div>
                <div class="info-value">
                  <span :class="tenantData.is_in_maintenance ? 'status-warning' : 'status-inactive'">
                    {{ tenantData.is_in_maintenance ? 'Sim' : 'Não' }}
                  </span>
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Limite de Usuários</div>
                <div class="info-value">{{ tenantData.user_limit || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Limite de Atendimentos</div>
                <div class="info-value">{{ tenantData.ticket_limit || 'N/A' }}</div>
              </div>
              <div class="info-item" v-if="tenantData && !tenantData.deleted_at && !wrongBackoffice">
                <div class="info-label">Plataforma</div>
                <div class="info-value">
                  <vs-button type="border" size="small" @click=redirectToLink()> Acessar </vs-button>
                </div>
              </div>
              <div class="info-item" v-if="tenantData && !tenantData.deleted_at && wrongBackoffice">
                <div class="info-label">Informativo</div>
                <div class="info-value"><span class="status-warning" >Esse cliente é Beta. <br> Acesse o Backoffice de Beta para gerenciar.</span></div>
              </div>
            </div>

            <div class="plan-expiration-section mt-4" v-if="!shouldDisableCards">
              <div class="card-divider mb-4"></div>
              <div class="tenant-info-grid">
                <div class="info-item">
                  <label class="vs-input--label">Data de Expiração do Plano</label>
                  <flat-pickr
                    v-model="planExpirationForm.planExpirationDate"
                    class="w-full"
                    :config="planExpirationDatePickerConfig"
                    placeholder="Escolha uma data"
                  />
                </div>
                <div class="info-item">
                  <label class="vs-input--label">Link de Suporte</label>
                  <vs-input
                    type="text"
                    placeholder="https://..."
                    v-model="planExpirationForm.supportLink"
                    class="w-full"
                  />
                </div>
              </div>
              <div class="mt-3 flex gap-2">
                <vs-button type="filled" size="small" @click="savePlanExpiration">Salvar expiração</vs-button>
                <vs-button
                  v-if="planExpirationForm.planExpirationDate"
                  type="border"
                  size="small"
                  @click="resetExpirationPopup"
                >
                  Reenviar aviso
                </vs-button>
              </div>
            </div>
          </div>
          <div v-else-if="shouldDisableCards" class="card-content disabled-content">
            <p class="disabled-message">Esta funcionalidade está indisponível.</p>
          </div>
        </div>
      </div>

      <!-- CARD — Recursos -->
      <div class="settings-card" :class="{ 'card-disabled': shouldDisableCards }">
        <div class="card-header">
          <feather-icon class="card-icon" icon="SettingsIcon"/>
          <span class="card-title">Recursos da Plataforma</span>
          <span v-if="shouldDisableCards" class="disabled-badge">Indisponível</span>
        </div>
        <div class="card-divider"></div>
        <div v-if="!shouldDisableCards" class="card-content">
          <UpdateTenantModal
            ref="updateTenantModal"
            :tenant="tenantData"
            :isPopup="false"
            :isActive="false"
            :features="dashSettings.activeFeatures"
            :partners="partners"
            :currentPartner="currentPartner"
            :enabledPlan="dashSettings.enabledPlan"
            :defaultPlans="dashSettings.defaultPlans"
            @setState="(newVal, features) => setState(newVal, features, null)"
            @setPlan="updatePlan"
            @updatePartner="updatePartner"
          />
        </div>
        <div v-else class="card-content disabled-content">
          <p class="disabled-message">Esta funcionalidade está indisponível.</p>
        </div>
      </div>

      <!-- CARD — Conexões -->
      <div class="settings-card" :class="{ 'card-disabled': shouldDisableCards }">
        <div class="card-header justify-between">
          <div class="flex align-items-center">
            <feather-icon class="card-icon" icon="SettingsIcon"/>
            <span class="card-title">Conexões</span>
          </div>

          <div class="inline-flex gap-2">
            <vx-tooltip v-if="!shouldDisableCards && canSee('instances-add')" class="cursor-pointer" delay=".2s" color="gray" position="bottom" text="Adicionar Conexão">
              <feather-icon @click="openInstanceCreationModal()" icon="PlusIcon"/>
            </vx-tooltip>
            <vx-tooltip v-if="!shouldDisableCards" class="cursor-pointer" delay=".2s" color="gray" position="bottom" text="Atualizar listagem">
              <feather-icon @click="loadFeatures()" icon="RefreshCwIcon"/>
            </vx-tooltip>
          </div>
          <span v-if="shouldDisableCards" class="disabled-badge">Indisponível</span>
        </div>
        <div class="card-divider"></div>
        <div v-if="!shouldDisableCards" class="card-content">
          <InstancesModal
              ref="instanceModal"
              :token="token"
              :isPopup="false"
              :isActive="false"
              :tenantName="tenantName"
              @instances="instancesLoaded"
              @createInstance="openInstanceCreationModal"
          />
        </div>
        <div v-else-if="shouldDisableCards" class="card-content disabled-content">
          <p class="disabled-message">Esta funcionalidade está indisponível.</p>
        </div>
      </div>

      <!-- CARD — Agendamento de Desativação -->
      <TenantDeactivationCard
         :tenant-data="tenantData"
         :is-origin-backoffice="isOriginBackoffice"
         :wrong-backoffice="wrongBackoffice"
      />

      <!-- CARD — Histórico de Ativação -->
      <TenantHistoryCard :tenant-data="tenantData"/>

      <!-- CARD — Relatórios -->
      <div class="settings-card" :class="{ 'card-disabled': shouldDisableCards }">
        <div class="card-header">
          <feather-icon class="card-icon" icon="FileTextIcon"/>
          <span class="card-title">Relatórios</span>
          <span v-if="shouldDisableCards" class="disabled-badge">Indisponível</span>
        </div>
        <div class="card-divider"></div>
        <div v-if="!shouldDisableCards" class="card-content">
          <div class="report-buttons">
            <div class="button-group" v-if="isFuncionalUser">
              <vs-button color="primary" type="border" @click="downloadModals.webhooks = true">
                Disparos (Funcional)
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="downloadModals.attendances = true">
                Atendimentos/24h (Custos Cloud)
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="downloadModals.attendancesAndCloudCosts = true">
                Atendimentos e Custos Cloud
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="openProductivityReport('attendance')">
                Atendimentos
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="openProductivityReport('maria')">
                Atendimentos Maria
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="openProductivityReport('campaign')">
                Campanhas
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="openProductivityReport('operator')">
                Operadores
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="openProductivityReport('order')">
                Pedidos
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="openProductivityReport('grade')">
                Avaliação
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="openProductivityReport('loginFailures')">
                Falhas de Login
              </vs-button>
            </div>
          </div>
        </div>
        <div v-else class="card-content disabled-content">
          <p class="disabled-message">Esta funcionalidade está indisponível.</p>
        </div>
      </div>

      <!-- CARD — Scripts -->
      <div class="settings-card" :class="{ 'card-disabled': shouldDisableCards }">
        <div class="card-header">
          <feather-icon class="card-icon" icon="FileTextIcon"/>
          <span class="card-title">Scripts</span>
          <span v-if="shouldDisableCards" class="disabled-badge">Indisponível</span>
        </div>
        <div class="card-divider"></div>
        <div v-if="!shouldDisableCards" class="card-content">
          <div class="report-buttons">
            <div class="button-group">
              <vs-button color="primary" type="border" @click="scriptModals.syncAnalytics = true">
                Sincronizar Analytics
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="scriptModals.diagnoseInstance = true">
                Dr. de Conexão
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="scriptModals.restartCampaign = true">
                Reiniciar Campanha
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="scriptModals.finishedTicketsRank = true">
                Identificar Encerramento em Massa
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="scriptModals.undoFinishedTickets = true">
                Desfazer Encerramento em Massa
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="scriptModals.distributionStatus = true">
                Status Distribuição
              </vs-button>
            </div>
          </div>
        </div>
        <div v-else class="card-content disabled-content">
          <p class="disabled-message">Esta funcionalidade está indisponível.</p>
        </div>
      </div>

      <!-- CARD — Integração ERP -->
      <div v-if="erpsEnabled" class="settings-card" :class="{ 'card-disabled': shouldDisableCards }">
        <div class="card-header">
          <feather-icon class="card-icon" icon="FileTextIcon"/>
          <span class="card-title">Integração ERP</span>
          <span v-if="shouldDisableCards" class="disabled-badge">Indisponível</span>
        </div>
        <div class="card-divider"></div>
        <div v-if="!shouldDisableCards" class="card-content">
          <div class="report-buttons">
            <div class="button-group">
              <vs-button color="primary" type="border" @click="erpIntegrationModals.syncProducts = true">
                Sincronizar Produtos com ERP
              </vs-button>
              <vs-button v-if="hasAlpha7Erp" color="primary" type="border" @click="erpIntegrationModals.createIntegrationDatabase = true">
                Banco de Dados de Integração
              </vs-button>
            </div>
          </div>
        </div>
        <div v-else class="card-content disabled-content">
          <p class="disabled-message">Esta funcionalidade está indisponível.</p>
        </div>
      </div>
    </div>

    <TenantActionModal
      :active="tenantModal.confirmAction"
      :action-label="action"
      :loading="tenantActionLoading"
      :requires-reason="true"
      @input="tenantModal.confirmAction = $event"
      @confirm="handleTenantAction"
    />

    <vs-popup title="Atenção" :active.sync="tenantModal.databaseNotFound" button-hidden>
      <p class="mt-0 pt-0 font-medium">O banco de dados deste cliente não existe mais. Não é possível reativar. Por favor, crie um novo tenant/instância.</p>
    </vs-popup>

    <UserSelectionModal
      :tenantName="tenantName"
      :active.sync="userSelection.active"
      :users="userSelection.users"
      :limit="userSelection.limit"
      :loading="userSelection.loading"
      :selected.sync="userSelection.selected"
      :adminUser="userSelection.adminUser"
      @confirm="confirmUserSelection"
      @cancel="closeUserSelection"
    />

    <CreateInstanceModal
      v-if="tenantData && !shouldDisableCards"
      :tenantName="tenantName"
      :isActive="tenantModal.createInstance"
      @isActiveFalse="tenantModal.createInstance=false"
      @successfullyCreated="instanceSuccessfullyCreated"
    />

    <AttendancesReport
      v-if="tenantData && !shouldDisableCards"
      :isActive="downloadModals.attendances"
      :websiteId="tenantData.website_id"
      @input="downloadModals.attendances = $event"
      @isActiveFalse="downloadModals.attendances=false"
    />

    <AttendancesAndCloudCostsReport
      v-if="tenantData && !shouldDisableCards"
      :isActive="downloadModals.attendancesAndCloudCosts"
      :websiteId="tenantData.website_id"
      @input="downloadModals.attendancesAndCloudCosts = $event"
      @isActiveFalse="downloadModals.attendancesAndCloudCosts = false"
    />

    <WebhooksReport
      v-if="tenantData && !shouldDisableCards"
      :isActive="downloadModals.webhooks"
      :tenantName="tenantName"
      @close="downloadModals.webhooks = false"
      @isActiveFalse="downloadModals.webhooks=false"
    />

    <OrdersReport
      :isActive="downloadModals.orders"
      @input="downloadModals.orders = $event"
      @isActiveFalse="downloadModals.orders=false"
    />

    <InstanceLogsReport
      :isActive="downloadModals.instanceLogs"
      @input="downloadModals.instanceLogs = $event"
      @isActiveFalse="downloadModals.instanceLogs=false"
    />

    <SyncAnalytics
      v-if="tenantData && !shouldDisableCards"
      :tenantName="tenantName"
      :isActive="scriptModals.syncAnalytics"
      @input="scriptModals.syncAnalytics = $event"
      @isActiveFalse="scriptModals.syncAnalytics=false"
    />

    <ProductivityReports
      v-if="tenantData && !shouldDisableCards && productivityReportModal.type"
      :isActive="productivityReportModal.active"
      :reportType="productivityReportModal.type"
      @isActiveFalse="closeProductivityReport"
    />

    <DiagnoseInstance
      v-if="tenantData && !shouldDisableCards"
      :tenantName="tenantName"
      :instances="instances"
      :isActive="scriptModals.diagnoseInstance"
      @input="scriptModals.diagnoseInstance = $event"
      @isActiveFalse="scriptModals.diagnoseInstance = false"
    />

    <RestartCampaign
      v-if="tenantData && !shouldDisableCards"
      :tenantName="tenantName"
      :isActive="scriptModals.restartCampaign"
      @input="scriptModals.restartCampaign = $event"
      @isActiveFalse="scriptModals.restartCampaign = false"
    />

    <FinishedTicketsRank
      v-if="tenantData && !shouldDisableCards"
      :tenantName="tenantName"
      :isActive="scriptModals.finishedTicketsRank"
      @input="scriptModals.finishedTicketsRank = $event"
      @isActiveFalse="scriptModals.finishedTicketsRank = false"
    />

    <UndoFinishedTickets
      v-if="tenantData && !shouldDisableCards"
      :tenantName="tenantName"
      :isActive="scriptModals.undoFinishedTickets"
      @input="scriptModals.undoFinishedTickets = $event"
      @isActiveFalse="scriptModals.undoFinishedTickets = false"
    />

    <SyncProducts
      :tenantName="tenantName"
      :isActive="erpIntegrationModals.syncProducts"
      @input="erpIntegrationModals.syncProducts = $event"
      @isActiveFalse="erpIntegrationModals.syncProducts = false"
    />

    <CreateIntegrationDatabase
      :tenantName="tenantName"
      :isActive="erpIntegrationModals.createIntegrationDatabase"
      @input="erpIntegrationModals.createIntegrationDatabase = $event"
      @isActiveFalse="erpIntegrationModals.createIntegrationDatabase = false"
    />

    <DistributionStatus
      v-if="tenantData && !shouldDisableCards"
      :tenantName="tenantName"
      :isActive="scriptModals.distributionStatus"
      @input="scriptModals.distributionStatus = $event"
      @isActiveFalse="scriptModals.distributionStatus = false"
    />
  </div>
</template>

<script>
import jwt from "jsonwebtoken";
import {mapGetters} from 'vuex';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {Portuguese} from 'flatpickr/dist/l10n/pt.js';
import {availablePlans, hasPermissionBackOffice} from "@/utils";
import InstancesModal from "@/views/Tenants/InstancesList.vue";
import CreateInstanceModal from "@/views/Tenants/CreateInstance.vue";
import UpdateTenantModal from "@/views/Tenants/UpdateTenant.vue";
import TenantActionModal from "@/views/Tenants/TenantActionModal.vue";
import UserSelectionModal from "@/views/Tenants/UserSelectionModal.vue";
import RequestFormModal from "@/components/approvals/RequestFormModal.vue";
import AttendancesReport from "../configs/AttendancesReport.vue";
import AttendancesAndCloudCostsReport from "../configs/AttendancesAndCloudCostsReport.vue";
import WebhooksReport from "../configs/WebhooksReport.vue";
import OrdersReport from "../configs/OrdersReport.vue";
import InstanceLogsReport from "../configs/InstanceLogsReport.vue";
import SyncAnalytics from "@/views/configs/SyncAnalytics.vue";
import ProductivityReports from "../configs/ProductivityReports.vue";
import DiagnoseInstance from "../configs/DiagnoseInstance.vue";
import RestartCampaign from "../configs/RestartCampaign.vue";
import FinishedTicketsRank from "../configs/FinishedTicketsRank.vue";
import UndoFinishedTickets from "../configs/UndoFinishedTickets.vue";
import SyncProducts from "../configs/SyncProducts.vue";
import CreateIntegrationDatabase from "../configs/CreateIntegrationDatabase.vue";
import DistributionStatus from "../configs/DistributionStatus.vue";
import TenantDeactivationCard from "@/views/Tenants/TenantDeactivationCard.vue";
import TenantHistoryCard from "@/views/Tenants/TenantHistoryCard.vue";
import erpHttp from "@/http/requests/erps/index";

export default {
  name: 'TenantInfo',
  components: {
    flatPickr,
    TenantDeactivationCard,
    TenantHistoryCard,
    SyncAnalytics,
    TenantActionModal,
    UpdateTenantModal,
    UserSelectionModal,
    RequestFormModal,
    CreateInstanceModal,
    InstancesModal,
    AttendancesReport,
    AttendancesAndCloudCostsReport,
    WebhooksReport,
    OrdersReport,
    InstanceLogsReport,
    ProductivityReports,
    DiagnoseInstance,
    RestartCampaign,
    FinishedTicketsRank,
    UndoFinishedTickets,
    SyncProducts,
    CreateIntegrationDatabase,
    DistributionStatus
  },
  computed: {
    ...mapGetters(['token']),
    isTenantDeleted() {
      return this.tenantData && this.tenantData.deleted_at !== null;
    },
    wrongBackoffice() {
      return this.tenantData && this.tenantData.is_beta && !window.location.host.includes('beta');
    },
    shouldDisableCards() {
      return !this.isOriginBackoffice || !this.tenantData || (this.isTenantDeleted || this.wrongBackoffice);
    },
    isOriginBackoffice(){
      return process.env.VUE_APP_ORIGIN_BACKOFFICE && process.env.VUE_APP_ORIGIN_BACKOFFICE === 'true'
    },
    erpsEnabled() {
      const erpsFeature = this.dashSettings.activeFeatures.some(
        feature => feature.name === 'erps' && feature.enabled === 'true'
      );
      return erpsFeature;
    },
    tenantDeletedAt() {
      const deletedAt = this.tenantData ? (this.tenantData.deleted_action_at || this.tenantData.deleted_at) : null;
      return deletedAt ? new Date(deletedAt).toLocaleString('pt-BR') : 'Não informado';
    }
  },
  data() {
    return {
      action: '',
      tenantName: '',
      fqdn: '',
      tenantData: null,
      isFuncionalUser: false,
      instances: [],
      tenantActionLoading: false,
      tenantModal: {
        confirmAction: false,
        createInstance: false,
        databaseNotFound: false,
      },
      dashSettings: {
        enabledPlan: null,
        activeFeatures: [],
        defaultPlans: availablePlans()
      },
      downloadModals: {
        orders: false,
        webhooks: false,
        attendances: false,
        attendancesAndCloudCosts: false,
        instanceLogs: false
      },
      scriptModals: {
        syncAnalytics: false,
        diagnoseInstance: false,
        restartCampaign: false,
        finishedTicketsRank: false,
        undoFinishedTickets: false,
        distributionStatus: false
      },
      productivityReportModal: {
        active: false,
        type: null
      },
      userSelection: {
        active: false,
        users: [],
        selected: [],
        limit: 0,
        loading: false,
        planData: null,
        adminUser: null
      },
      erpIntegrationModals: {
        syncProducts: false,
        createIntegrationDatabase: false
      },
      partners: [],
      currentPartner: {},
      hasAlpha7Erp: false,
      planExpirationForm: {
        planExpirationDate: '',
        supportLink: ''
      },
      planExpirationDatePickerConfig: {
        enableTime: false,
        noCalendar: false,
        dateFormat: 'Y-m-d',
        locale: Portuguese
      }
    }
  },
  methods: {
    canSee(word) {
      return hasPermissionBackOffice(`tenant-list-${word}`);
    },
    openInstanceCreationModal() {
      this.tenantModal.createInstance = true;
    },
    instancesLoaded(value){
      this.instances = value
    },
    instanceSuccessfullyCreated() {
      this.tenantModal.createInstance = false;
      this.$refs['instanceModal'].loadInstances();
    },
    getTenantPoolName(tenant) {
      const t = tenant || this.tenantData;
      if (!t) return null;
      if (t.managed_by_database_connection) return t.managed_by_database_connection;
      if (t.website && t.website.managed_by_database_connection) return t.website.managed_by_database_connection;
      return null;
    },
    getExistingPoolNames(cp) {
      if (!cp || typeof cp !== 'object') return [];
      if (cp.data && typeof cp.data === 'object' && !Array.isArray(cp.data)) return Object.keys(cp.data);
      if (cp.pools && typeof cp.pools === 'object' && !Array.isArray(cp.pools)) return Object.keys(cp.pools);
      return Object.keys(cp);
    },
    confirmAction() {
      this.action = this.tenantData.deleted_at ? 'Restaurar' : 'Desativar';

      if (!this.tenantData.deleted_at) {
        this.tenantModal.confirmAction = true;
        return;
      }

      const poolName = this.getTenantPoolName(this.tenantData);

      this.$store.dispatch('countPerPool', { filterByEnvironment: true, token: this.token }).then(() => {
        if (!poolName) {
          this.tenantModal.databaseNotFound = true;
          return;
        }
        const countPerPool = this.$store.state.tenants && this.$store.state.tenants.countPerPool ? this.$store.state.tenants.countPerPool : {};
        const existingPools = this.getExistingPoolNames(countPerPool);
        const poolNameNormalized = poolName ? String(poolName).replace(/^tenancy\./, '') : '';
        const poolExists = existingPools.indexOf(poolName) >= 0 || existingPools.indexOf(poolNameNormalized) >= 0;
        if (poolExists) {
          this.tenantModal.confirmAction = true;
        } else {
          this.tenantModal.databaseNotFound = true;
        }
      }).catch((exception) => {
        console.error('Erro no countPerPool: ', exception);
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Não foi possível verificar os bancos de dados. Tente novamente.',
        });
      });
    },
    handleTenantAction(reason) {
      this.updateTenant(reason);
    },
    async redirectToLink() {
      const tenant = this.tenantData.fqdn.split(".")[0];
      const tokenPayload = { tenant: tenant, exp: Math.floor(Date.now() / 1000) + (3 * 60) };

      const baseUrl = `${process.env.VUE_APP_PEDBOT_ADMIN_URL}/${tenant}/login`;
      const access_key = jwt.sign(tokenPayload, process.env.VUE_APP_LOGIN_TOKEN);
      const access_token = process.env.VUE_APP_INTEGRATION_TOKEN;

      const params = new URLSearchParams();
      params.append('access_token', access_token);
      params.append('access_key', access_key);

      const url = `${baseUrl}?${params.toString()}`;
      window.open(url, '_blank');
    },
    async updateTenant(reason = null) {
      const isRestore = !!this.tenantData.deleted_at;
      const action = isRestore ? 'restoreTenant' : 'deleteTenant';

      if (!reason) {
        this.$vs.notify({
          color: 'warning',
          title: 'Motivo obrigatório',
          text: 'Informe o motivo antes de continuar.'
        });
        return;
      }

      this.$vs.loading();
      this.tenantActionLoading = true;

      try {
        await this.$store.dispatch(action, { id: this.tenantData.id, reason, token: this.token });
        await this.loadTenantData();
        this.$vs.notify({
          color: 'success',
          title: 'Sucesso!',
          text: `Tenant ${isRestore ? 'Reativado' : 'Desativado'} com sucesso!`
        });
      } catch (exception) {
        console.error(exception);
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Ocorreu um erro ao realizar a ação! Entre em contato com o time técnico.'
        });
      } finally {
        this.tenantModal.confirmAction = false;
        this.tenantActionLoading = false;
        this.$vs.loading.close();
      }
    },
    loadTenantData() {
      this.partners = []
      this.currentPartner = {}
      this.hasAlpha7Erp = false

      return this.$store.dispatch('fetchPaginatedTenants', {
        page: 1,
        limit: 1,
        sortName: 'deleted_at',
        sortType: 'DESC',
        token: this.token,
        filterUsers: false,
        search: this.fqdn,
        strictFqdn: true
      }).then(async response => {
        this.tenantData = response.items[0] || null;

        if (!this.tenantData || this.tenantData.deleted_at || this.wrongBackoffice) {
          return this.tenantData;
        }

        this.planExpirationForm.planExpirationDate = this.tenantData.plan_expiration_date || '';
        this.planExpirationForm.supportLink = this.tenantData.support_link || '';

        await this.loadFeatures()
        await this.fetchPartners()
        await this.fetchHasAlpha7Erp()
        return this.tenantData
      }).catch(error => {
        console.error('Error loading tenant data:', error);
        this.$vs.notify({color: 'danger', title: 'Erro!', text: 'Erro ao carregar dados do tenant.'});
        this.tenantData = null;
        return null;
      });
    },
    loadFeatures() {
      return this.$store.dispatch('getFeatures', {
        token: this.token,
        tenantName: this.tenantName
      }).then(response => {
        const features = (response && response.data ? response.data : []);
        this.dashSettings.enabledPlan = this.tenantData.plan;
        this.dashSettings.activeFeatures = features.filter(item => item.name !== 'websocket');
        this.$refs['updateTenantModal'].refreshDefault();
        this.$refs['instanceModal'].loadInstances();
      }).catch(exception => console.error(exception));
    },
    setState(newVal, features, name = null) {
      if (name){ this[name] = newVal; }
      if (features && Object.keys(features).length) {
        this.$store.dispatch('updateFeatures', {features: features, tenantName: this.tenantName, token: this.token}).then(() => {
          this.$vs.notify({ color: 'success',
            title: 'Sucesso!',
            text: `Features ativadas com sucesso!`
          });
        }).catch(exception => {
          console.error(exception)
          this.$vs.notify({
            color: 'danger',
            title: 'Erro!',
            text: 'Ocorreu um erro ao realizar a ação! Entre em contato com o time técnico.'
          });
        });
      }
    },
    async updatePlan({plan, user, ticket}) {
      this.$vs.loading();
      try {
        if ( !plan ) {
          this.$vs.loading.close();
          this.$vs.notify({
            color: 'danger',
            title: 'Erro!',
            text: 'Informe um valor válido para o Plano.'
          });
          return;
        }

        const parsedPlan = plan.toUpperCase();
        const limit = Number.isInteger(user) ? user : parseInt(user);

        if (limit && limit > 0) {
          const users = await this.fetchTenantUsers();
          const activeUsers = users ? users.length : 0;

          if (activeUsers && limit < activeUsers) {
            this.userSelection.limit = limit;
            this.userSelection.users = users;
            this.userSelection.selected = [];
            this.userSelection.planData = { plan: parsedPlan, user: limit, ticket: ticket };
            this.userSelection.active = true;
            this.$vs.loading.close();
            return;
          }
        }

        await this.dispatchPlanUpdate({ plan: parsedPlan, user: limit, ticket: ticket });
      } catch (exception) {
        console.error(exception);
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Ocorreu um erro ao realizar a ação! Entre em contato com o time técnico.'
        });
      } finally {
        if (!this.userSelection.active) {
          this.$vs.loading.close();
        }
      }
    },
    openProductivityReport(type) {
      this.productivityReportModal.type = type;
      this.productivityReportModal.active = true;
    },
    closeProductivityReport() {
      this.productivityReportModal.active = false;
      this.productivityReportModal.type = null;
    },
    async fetchTenantUsers() {
      try {
        this.userSelection.loading = true;
        const response = await this.$store.dispatch('getTenantUsers', { tenantName: this.tenantName, token: this.token });
        const payload = response && response.data ? response.data : response;

        this.userSelection.users = payload && payload.users ? payload.users : (Array.isArray(payload) ? payload : []);
        this.userSelection.adminUser = payload && payload.admin_user ? payload.admin_user : null;

        return this.userSelection.users;
      } catch (exception) {
        console.error(exception);
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Não foi possível carregar os usuários do tenant.'
        });
        return [];
      } finally {
        this.userSelection.loading = false;
      }
    },
    async confirmUserSelection(selectedUsers) {
      const selected = selectedUsers || this.userSelection.selected;

      this.$vs.loading();
      try {
        await this.dispatchPlanUpdate({
          ...this.userSelection.planData,
          keepUserIds: selected
        });

        this.userSelection.active = false;
      } catch (exception) {
        console.error(exception);
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Ocorreu um erro ao salvar o plano e atualizar usuários. Entre em contato com o time técnico.'
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    async dispatchPlanUpdate({ plan, user, ticket, keepUserIds = [] }) {
      await this.$store.dispatch('updateTenantPlan', {
        token: this.token,
        id: this.tenantData.id,
        plan: plan,
        user: user,
        ticket: ticket,
        tenantName: this.tenantName,
        keepUserIds: keepUserIds
      });

      this.loadTenantData();

      this.$vs.notify({
        color: 'success',
        title: 'Sucesso!',
        text: `Plano do Tenant atualizado com sucesso.`
      });
    },
    closeUserSelection() {
      this.userSelection.active = false;
      this.userSelection.selected = [];
      this.userSelection.planData = null;
    },
    async resetExpirationPopup() {
      this.$vs.loading();
      try {
        await this.$store.dispatch('resetExpirationPopup', {
          token: this.token,
          id: this.tenantData.id
        });
        this.$vs.notify({ color: 'success', title: 'Sucesso!', text: 'O aviso será exibido novamente no próximo login do cliente.' });
      } catch (exception) {
        console.error(exception);
        this.$vs.notify({ color: 'danger', title: 'Erro!', text: 'Ocorreu um erro ao reenviar o aviso.' });
      } finally {
        this.$vs.loading.close();
      }
    },
    async savePlanExpiration() {
      this.$vs.loading();
      try {
        await this.$store.dispatch('updatePlanExpiration', {
          token: this.token,
          id: this.tenantData.id,
          planExpirationDate: this.planExpirationForm.planExpirationDate || null,
          supportLink: this.planExpirationForm.supportLink || null
        });
        this.$vs.notify({ color: 'success', title: 'Sucesso!', text: 'Expiração do plano atualizada com sucesso.' });
      } catch (exception) {
        console.error(exception);
        this.$vs.notify({ color: 'danger', title: 'Erro!', text: 'Ocorreu um erro ao salvar. Entre em contato com o time técnico.' });
      } finally {
        this.$vs.loading.close();
      }
    },
    async fetchPartners() {
      if (!this.tenantName || !this.tenantData || this.tenantData.deleted_at || this.wrongBackoffice) {
        this.partners = []
        this.currentPartner = {}
        this.hasAlpha7Erp = false
        return
      }

      let pertnersResponse = await this.$store.dispatch('getPartners', {
        token: this.token,
        tenantName: this.tenantName
      })
      this.partners = pertnersResponse.data ? pertnersResponse.data : []
      this.currentPartner = this.partners.find(partner => partner.enabled === true) || {}
    },
    async fetchHasAlpha7Erp() {
      this.hasAlpha7Erp = false;
      if (!this.tenantName) {
        return;
      }

      try {
        const response = await erpHttp.getAllErps(this.tenantName, this.token);
        const erps = response.data || [];
        this.hasAlpha7Erp = erps.some(erp => String(erp.erp_name).toLowerCase() === 'alpha7' || erp.erp_id === 2);
      } catch (exception) {
        console.error('Erro ao verificar ERP Alpha7:', exception);
      }
    },
    updatePartner(partnerId) {
      this.$store.dispatch('updatePartner', {partnerId: partnerId, tenantName: this.tenantName, token: this.token}).then((response) => {
        this.currentPartner = response.data;
        this.$vs.notify({ color: 'success',
          title: 'Sucesso!',
          text: `Parceiro atualizado com sucesso!`
        });
      }).catch(exception => {
        console.error(exception)
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Ocorreu um erro ao realizar a ação! Entre em contato com o time técnico.'
        });
      });
    }
  },
  async created() {
    this.tenantName = this.$route.params.tenantName;
    this.fqdn = localStorage.getItem('tenantFqdn') || (this.tenantName + '.');
    localStorage.removeItem('tenantFqdn');
    await this.loadTenantData();
    this.$store.dispatch('isFuncionalUser', this.token).then(response => this.isFuncionalUser = !!response.data);
  }
}
</script>

<style lang="scss" scoped>
.info {
  padding: 2rem 50px;
  background-color: #ffffff;
  min-height: 100vh;
}

.settings-container {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
}

.settings-card {
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e8e8e8;
  overflow: hidden;
}

.align-items-center{
  align-items: center !important;
}

.card-header {
  display: flex;
  padding: 1rem;
  align-items: center;
  border-bottom: 1px solid #e8e8e8;
}

.card-icon {
  width: 16px;
  height: 16px;
  color: #666;
  margin-right: 0.5rem;
}

.card-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.card-divider {
  height: 1px;
  background-color: #e8e8e8;
}

.card-content {
  flex: 1;
  padding: 1rem;
}

.switch-row {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.switch-label {
  margin-left: 0.5rem;
  font-size: 13px;
  color: #666;
}

.tenant-info-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 1rem;
}

.tenant-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  font-size: 11px;
  color: #666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 13px;
  color: #333;
  font-weight: 500;
}

.status-active {
  color: #2e7d32;
  font-weight: 600;
}

.status-inactive {
  color: #666;
}

.status-warning {
  color: #f57c00;
  font-weight: 600;
}

.report-buttons {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}


.card-disabled {
  opacity: 0.6;
  pointer-events: none;
}

.disabled-badge {
  margin-left: auto;
  font-size: 11px;
  color: #f44336;
  font-weight: 600;
  background-color: #ffebee;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.disabled-content {
  text-align: center;
  padding: 2rem 1rem;
}

.disabled-message {
  color: #666;
  font-size: 13px;
  margin: 0;
  font-style: italic;
}

.deactivation-details-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.deactivation-card {
  height: 100%;
}

.history-card {
  grid-column: 1 / -1;
}

.reason-content {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-height: 260px;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.schedule-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.schedule-link {
  color: #7367f0;
}

</style>